/** One passage that establishes a clinical reality: the sentence, the document it
 *  comes from, and its offsets inside that document.
 *
 *  Nothing here is a copy the backend composed. The verbatim and the span come
 *  from a join back onto the assertion registry, so re-reading the document under
 *  a new parser moves the offsets for every consumer at once. The document itself
 *  is not stored — it is fetched live from the source system — which is exactly
 *  why the span matters: it is what makes the quote re-locatable rather than
 *  merely quotable. */
export type Preuve = {
  assertion_id?: string;
  date?: string;
  verbatim?: string;
  source_doc_id?: string;
  doc_type?: string;
  span_start?: number;
  span_end?: number;
  /** The mention's own wording, which is what a `composante` is. */
  label?: string;
};

/** One clinical reality of a synthesised field, as the temporal projection emits
 *  it (backend `temporal_materialization._project_entities`): a label copied from
 *  a document, its status re-derived AT the slider date, the date it began, the
 *  other wordings documents gave it, and the passages that prove it.
 *
 *  Every field here is filtered to the slider position, `preuve` included — a
 *  reader looking at 2023 is never shown a 2024 letter as proof of what was known
 *  in 2023. The projection is a re-derivable view over the immutable contribution
 *  journal, so this shape is read-only on the patient page. */
export type Problem = {
  label: string;
  statut?: string;
  depuis?: string;
  sources?: string[];
  composantes?: string[];
  preuve?: Preuve[];
  /** Documents that contradict each other. Surfaced rather than hidden: a record
   *  where one letter says L4-L5 and another L5-S1 is one somebody must look at,
   *  and every losing mention stays attached to the reality. */
  conteste?: boolean;
  /** "courant" | "antecedent" | "planifie", or absent when no document dated the
   *  fact itself — passed through honestly rather than defaulted, so a reader can
   *  tell "current" from "not known". */
  nature_temporelle?: string;
};

/** True when a projected field value is a synthesised problem list (array of
 *  objects carrying a string `label`) rather than a flat array of strings. Lets
 *  the renderer branch on shape, so any array_* field that opted into synthesis
 *  renders two-tier while every other field keeps the flat renderer. */
export function isProblemList(value: unknown): value is Problem[] {
  return Array.isArray(value) && value.length > 0 && value.every(isReality);
}

function isReality(v: unknown): v is Problem {
  return (
    v !== null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    typeof (v as { label?: unknown }).label === "string"
  );
}

/** A list as a clinician states it: each reality as its label, every other item
 *  as it is — the backend's `patient_field_store.plane_value`. A corrected list
 *  holds labels, and the realities a later document established are projected
 *  beside them, so one list can hold both. */
export function asTerms(value: unknown): unknown {
  if (!Array.isArray(value) || !value.some(isReality)) return value;
  return value.map((item) => (isReality(item) ? item.label : item));
}
